import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import Lenis from 'lenis';
import { Button } from './components/ui/button';
import { Card, CardContent, CardTitle } from './components/ui/card';
import { projectsData, getFeaturedProjects } from './data/projects';
import ContactForm from './components/ContactForm';
import { FaGithub, FaLinkedinIn, FaFacebookF } from 'react-icons/fa';


function App() {
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [displayedProjects, setDisplayedProjects] = useState([]);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    // Start in dark mode
    document.documentElement.classList.add('dark');

    // Initially show only featured projects
    const featuredProjects = getFeaturedProjects();
    setDisplayedProjects(featuredProjects);

    // Initialize Lenis smooth scrolling immediately
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Close mobile menu when clicking outside
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('nav')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    // Cleanup
    return () => {
      lenis.destroy();
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handleViewAllProjects = () => {
    if (showAllProjects) {
      // Show only featured projects
      const featuredProjects = getFeaturedProjects();
      setDisplayedProjects(featuredProjects);
    } else {
      // Show all projects
      setDisplayedProjects(projectsData);
    }
    setShowAllProjects(!showAllProjects);
  };

  // Safe motion helper
  const safeMotion = (desktop) => shouldReduceMotion ? { initial: { opacity: 0 }, animate: { opacity: 1 } } : desktop;

  // Animation variants with reduced motion optimization
  const fadeInUp = {
    initial: { opacity: 0, y: shouldReduceMotion ? 10 : 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: shouldReduceMotion ? 0.2 : 0.6, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0.02 : 0.1
      }
    }
  };

  const ProjectCard = ({ project, index }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
      <motion.div
        ref={ref}
        {...safeMotion({
          initial: { opacity: 0, y: 50 },
          animate: isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 },
          transition: { duration: 0.6, delay: index * 0.1 }
        })}
        className="group relative rounded-2xl overflow-hidden shadow-xl shadow-black/10 dark:shadow-none transition-transform duration-300 hover:scale-[1.02]"
      >
        <div className="aspect-video w-full overflow-hidden bg-gray-200 dark:bg-gray-800">
          <img
            alt={project.title}
            className={`w-full h-full object-cover transition-transform duration-700 ${!shouldReduceMotion ? 'group-hover:scale-105' : ''}`}
            src={project.image}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-6">
          <h3 className="text-white text-xl font-bold mb-2">{project.title}</h3>
          <p className="text-gray-200 text-sm mb-3 line-clamp-2">{project.description}</p>
          <p className="text-primary font-medium text-sm mb-4">
            {project.technologies.join(" • ")}
          </p>
          <div className="flex gap-2">
            {project.liveUrl && (
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90 text-white"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(project.liveUrl, '_blank');
                }}
              >
                <span className="material-icons-outlined text-sm mr-1">launch</span>
                Live Demo
              </Button>
            )}
            {project.githubUrl && (
              <Button
                size="sm"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(project.githubUrl, '_blank');
                }}
              >
                <span className="material-icons-outlined text-sm mr-1">code</span>
                GitHub
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  // Parallax effect for hero section - disabled when reduced motion is preferred
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, shouldReduceMotion ? 0 : -50]);
  const y2 = useTransform(scrollY, [0, 300], [0, shouldReduceMotion ? 0 : -100]);

  return (
    <div className="font-display bg-background-light dark:bg-background-dark text-gray-800 dark:text-gray-100 transition-colors duration-300 antialiased selection:bg-primary selection:text-white relative overflow-x-hidden min-h-screen">
      {/* Glassmorphism Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 w-full">
        <motion.div
          animate={!shouldReduceMotion ? {
            rotate: [0, 360],
            scale: [1, 1.2, 1]
          } : {}}
          transition={!shouldReduceMotion ? {
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          } : {}}
          className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/5 via-transparent to-blue-500/5 rounded-full blur-3xl"
        ></motion.div>
        <motion.div
          animate={!shouldReduceMotion ? {
            rotate: [360, 0],
            scale: [1, 0.8, 1]
          } : {}}
          transition={!shouldReduceMotion ? {
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          } : {}}
          className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-purple-500/5 via-transparent to-pink-500/5 rounded-full blur-3xl"
        ></motion.div>

        {/* Enhanced floating glass particles - Desktop only */}
        {!shouldReduceMotion ? (
          [...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -150, 0],
                x: [0, Math.sin(i) * 80, 0],
                opacity: [0.2, 0.8, 0.2],
                scale: [0.8, 1.4, 0.8],
                rotate: [0, 180, 360]
              }}
              transition={{
                duration: 12 + i * 2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 1.2
              }}
              className={`absolute glass-orb`}
              style={{
                width: `${8 + (i % 4) * 4}px`,
                height: `${8 + (i % 4) * 4}px`,
                left: `${5 + i * 8}%`,
                top: `${10 + (i % 3) * 20}%`
              }}
            ></motion.div>
          ))
        ) : (
          // Static particles for reduced motion
          [...Array(4)].map((_, i) => (
            <div
              key={i}
              className={`absolute glass-orb opacity-30`}
              style={{
                width: `${12 + (i % 2) * 4}px`,
                height: `${12 + (i % 2) * 4}px`,
                left: `${20 + i * 20}%`,
                top: `${20 + i * 15}%`
              }}
            ></div>
          ))
        )}

        {/* Large decorative glass elements - Desktop only */}
        {!shouldReduceMotion && (
          <>
            <motion.div
              animate={{
                y: [0, -30, 0],
                rotate: [0, 10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute top-1/4 right-1/4 w-32 h-32 glass-orb opacity-30"
            ></motion.div>

            <motion.div
              animate={{
                y: [0, 40, 0],
                rotate: [0, -15, 0],
                scale: [1, 0.9, 1]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 3
              }}
              className="absolute bottom-1/3 left-1/5 w-24 h-24 glass-orb opacity-25"
            ></motion.div>
          </>
        )}
      </div>
      {/* Navigation */}
      <motion.nav
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 w-full z-50 glassmorphism-nav"
      >
        <div className="container mx-auto px-4 sm:px-6 md:px-10 lg:px-14 xl:px-18 py-4 flex justify-between items-center w-full overflow-hidden">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl font-bold tracking-wide text-white drop-shadow-lg"
          >
            Md Junaid <span className="text-primary">Hossain</span>
          </motion.div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <span className="material-icons-outlined text-2xl">
              {isMobileMenuOpen ? 'close' : 'menu'}
            </span>
          </Button>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="hidden md:flex space-x-8 text-sm font-medium"
          >
            {[
              { name: 'Home', href: '/' },
              { name: 'About', href: '#about' },
              { name: 'Skills', href: '#skills' },
              { name: 'Projects', href: '#projects' },
              { name: 'Contacts', href: '#contacts' }
            ].map((item, index) => (
              <motion.a
                key={item.name}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                className="nav-link-glass"
                href={item.href}
              >
                {item.name}
              </motion.a>
            ))}
          </motion.div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ 
            opacity: isMobileMenuOpen ? 1 : 0,
            height: isMobileMenuOpen ? 'auto' : 0
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="md:hidden overflow-hidden glass-mobile-menu border-t border-white/10"
        >
          <div className="container mx-auto px-4 sm:px-6 py-4 w-full overflow-hidden">
            <div className="flex flex-col space-y-4">
              {[
                { name: 'Home', href: '/' },
                { name: 'About', href: '#about' },
                { name: 'Skills', href: '#skills' },
                { name: 'Projects', href: '#projects' },
                { name: 'Contacts', href: '#contacts' }
              ].map((item, index) => (
                <motion.a
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ 
                    opacity: isMobileMenuOpen ? 1 : 0,
                    x: isMobileMenuOpen ? 0 : -20
                  }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="mobile-nav-link-glass"
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Glassmorphism decorative elements - Desktop only */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          {!shouldReduceMotion && (
            <>
              <motion.div
                animate={{
                  x: [0, 100, 0],
                  y: [0, -20, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute top-2 left-1/4 w-32 h-32 bg-gradient-to-r from-primary/20 to-blue-500/20 rounded-full blur-xl"
              ></motion.div>
              <motion.div
                animate={{
                  x: [0, -80, 0],
                  y: [0, 15, 0],
                  scale: [1, 0.9, 1]
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2
                }}
                className="absolute top-4 right-1/3 w-24 h-24 bg-gradient-to-l from-purple-500/20 to-pink-500/20 rounded-full blur-xl"
              ></motion.div>
            </>
          )}
        </div>
      </motion.nav>

      <div className="w-full overflow-x-hidden">
        {/* Header */}
        <header className="relative pt-20 sm:pt-24 md:pt-0 pb-8 sm:pb-6 overflow-hidden min-h-screen sm:min-h-0 flex items-center sm:block w-full">
          <motion.div
            style={{ y: y1 }}
            className="absolute top-1/4 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -z-10 hidden sm:block"
          ></motion.div>
          <motion.div
            style={{ y: y2 }}
            className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl -z-10 hidden sm:block"
          ></motion.div>

          {/* Glassmorphism floating elements - Desktop only */}
          {!shouldReduceMotion && (
            <>
              <motion.div
                animate={{
                  y: [0, -20, 0],
                  x: [0, 10, 0],
                  rotate: [0, 5, 0]
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute top-16 right-1/4 w-20 h-20 glass-orb z-10 hidden md:block"
              ></motion.div>

              <motion.div
                animate={{
                  y: [0, 15, 0],
                  x: [0, -15, 0],
                  rotate: [0, -3, 0]
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2
                }}
                className="absolute bottom-12 left-1/3 w-16 h-16 glass-orb z-10 hidden md:block"
              ></motion.div>

              <motion.div
                animate={{
                  y: [0, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
                className="absolute top-1/2 left-8 w-12 h-12 glass-orb z-10 hidden md:block"
              ></motion.div>
            </>
          )}
          <div className="container mx-auto px-6 sm:px-10 md:px-14 lg:px-18 flex flex-col md:flex-row md:-mt-20 w-full">
            <motion.div
              {...safeMotion({
                initial: { opacity: 0, x: -100 },
                animate: { opacity: 1, x: 0 },
                transition: { duration: 0.8, delay: 0.2 }
              })}
              className="w-full md:w-1/2 mb-8 sm:mb-6 md:mb-0 z-10 text-center sm:text-left order-2 md:order-1"
            >
              <motion.h2
                {...safeMotion({
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.6, delay: 0.4 }
                })}
                className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold mb-3 sm:mb-2 flex items-center justify-center sm:justify-start"
              >
                Hello <span className="text-primary text-2xl sm:text-3xl md:text-4xl ml-1 leading-none">.</span>
              </motion.h2>
              <motion.div
                {...safeMotion({
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.6, delay: 0.5 }
                })}
                className="flex items-center mb-4 justify-center sm:justify-start"
              >
                <div className="h-0.5 w-8 sm:w-12 bg-primary mr-3 sm:mr-4"></div>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 dark:text-gray-300">I'm Junaid</p>
              </motion.div>
              <motion.h1
                {...safeMotion({
                  initial: { opacity: 0, y: 30 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.8, delay: 0.6 }
                })}
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-6 sm:mb-5 text-center sm:text-left"
              >
                Web <br className="hidden sm:block" /> Developer
              </motion.h1>
              <motion.div
                {...safeMotion({
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.6, delay: 0.8 }
                })}
                className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center sm:justify-start max-w-sm sm:max-w-none mx-auto sm:mx-0"
              >
                <Button
                  className="px-6 sm:px-8 py-3 glass-button font-semibold shadow-lg shadow-primary/30 hover:shadow-primary/40 transition-all transform hover:-translate-y-0.5 active:translate-y-0 w-full sm:w-auto text-sm sm:text-base"
                  onClick={() => setIsContactFormOpen(true)}
                >
                  Got a project?
                </Button>
                <Button
                  variant="outline"
                  className="px-6 sm:px-8 py-3 glass-button border-white/30 hover:border-white/50 font-medium w-full sm:w-auto text-sm sm:text-base"
                  onClick={() => window.open('/resume.pdf', '_blank')}
                >
                  My resume
                </Button>
              </motion.div>
            </motion.div>
            <motion.div
              {...safeMotion({
                initial: { opacity: 0, x: 100 },
                animate: { opacity: 1, x: 0 },
                transition: { duration: 0.8, delay: 0.4 }
              })}
              className="w-full md:w-1/2 relative flex justify-center items-center mb-8 sm:mb-0 md:mt-0 order-1 md:order-2 px-4 sm:px-0"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={!shouldReduceMotion ? { rotate: 360 } : {}}
                  transition={!shouldReduceMotion ? { duration: 20, repeat: Infinity, ease: "linear" } : {}}
                  className="w-40 h-40 sm:w-64 sm:h-64 md:w-72 md:h-72 lg:w-96 lg:h-96 rounded-full border-2 border-primary/30 relative"
                >
                  <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-primary/20 to-transparent opacity-50"></div>
                </motion.div>
              </div>
              <motion.div
                whileHover={!shouldReduceMotion ? { scale: 1.05 } : {}}
                transition={{ duration: 0.3 }}
                className="relative z-10 w-40 h-40 sm:w-56 sm:h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-full overflow-hidden border-4 border-background-light dark:border-background-dark shadow-2xl bg-gradient-to-br from-primary/10 to-blue-500/10"
              >
                <img
                  alt="Junaid Portrait"
                  className="w-full h-full object-cover object-center scale-110 transition-transform duration-500 filter brightness-105 contrast-110"
                  src="https://i.ibb.co.com/CKJmvxyQ/Junaid-Image.jpg"
                />
              </motion.div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-700 dark:text-gray-600 opacity-20 text-4xl sm:text-6xl font-thin select-none pointer-events-none hidden md:block">&lt;</div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-700 dark:text-gray-600 opacity-20 text-4xl sm:text-6xl font-thin select-none pointer-events-none hidden md:block">&gt;</div>
            </motion.div>
          </div>
        </header>

        {/* Spacing between hero and skills */}
        <div className="py-6 sm:py-0 md:py-10"></div>

        {/* Skills Banner */}
        <motion.div
          {...safeMotion({
            initial: { opacity: 0, y: 50 },
            whileInView: { opacity: 1, y: 0 },
            viewport: { once: true },
            transition: { duration: 0.6 }
          })}
          className="w-full glass-banner py-4 border-y border-white/10 overflow-hidden"
        >
          <div className="container mx-auto px-4 sm:px-6 md:px-10 lg:px-14 xl:px-18 w-full overflow-hidden">
            <div className="overflow-x-auto scrollbar-hide">
              <motion.div
                variants={staggerContainer}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="flex justify-between md:justify-around items-center min-w-max space-x-3 sm:space-x-6 md:space-x-8 lg:space-x-0 px-2 sm:px-4 md:px-0"
              >
              {['Javascript', 'React', 'Node.js', 'EXPRESS.js', 'MongoDb', 'Tailwind CSS', 'CSS', 'Git', 'Github'].map((skill, index) => (
                <motion.div
                  key={skill}
                  {...safeMotion({
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 },
                    transition: { duration: 0.5, delay: index * 0.1 },
                    whileHover: { scale: 1.1, y: -5 }
                  })}
                  className="glass-skill-badge"
                >
                  {skill}
                </motion.div>
              ))}
            </motion.div>
            </div>
          </div>
        </motion.div>

        {/* About Section */}
        <section className="py-12 sm:py-16 md:py-20 overflow-hidden" id="about">
          <div className="container mx-auto px-4 sm:px-6 md:px-10 lg:px-14 xl:px-18 w-full overflow-hidden">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 w-full">
              <motion.div
                {...safeMotion({
                  initial: { opacity: 0, x: -100 },
                  whileInView: { opacity: 1, x: 0 },
                  viewport: { once: true },
                  transition: { duration: 0.8 }
                })}
                className="w-full lg:w-1/3 space-y-4 sm:space-y-6 lg:space-y-8 relative order-2 lg:order-1 px-2 sm:px-0 flex flex-col items-center lg:items-start"
              >
                <div className="absolute left-[11px] top-4 bottom-4 w-0.5 bg-gray-300 dark:bg-gray-700 h-full -z-10 hidden lg:block"></div>

                {[
                  { icon: 'code', title: 'Website Development' },
                  { icon: 'smartphone', title: 'UI/UX Engineering' },
                  { icon: 'dns', title: 'Performance' }
                ].map((service, index) => (
                  <motion.div
                    key={service.title}
                    {...safeMotion({
                      initial: { opacity: 0, x: -50 },
                      whileInView: { opacity: 1, x: 0 },
                      viewport: { once: true },
                      transition: { duration: 0.6, delay: index * 0.2 }
                    })}
                    className="flex items-start group w-full max-w-xs sm:max-w-sm lg:max-w-none mx-auto lg:mx-0"
                  >
                    <div className="relative flex-shrink-0 mr-3 sm:mr-4 lg:mr-6">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-background-light dark:bg-background-dark border-2 border-primary z-10 mt-1"></div>
                    </div>
                    <Card className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 p-3 sm:p-4 lg:p-4 glass-service-card shadow-sm hover:shadow-md transition-shadow w-full border-0">
                      <span className="material-icons-outlined text-lg sm:text-xl lg:text-2xl xl:text-3xl text-white/80 group-hover:text-white transition-colors flex-shrink-0">{service.icon}</span>
                      <h3 className="font-semibold text-sm sm:text-base lg:text-lg text-white/90 group-hover:text-white transition-colors leading-tight">{service.title}</h3>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                {...safeMotion({
                  initial: { opacity: 0, x: 100 },
                  whileInView: { opacity: 1, x: 0 },
                  viewport: { once: true },
                  transition: { duration: 0.8 }
                })}
                className="w-full lg:w-2/3 lg:pl-12 order-1 lg:order-2 px-2 sm:px-0 overflow-hidden"
              >
                <motion.h2
                  {...safeMotion({
                    initial: { opacity: 0, y: 30 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true },
                    transition: { duration: 0.6 }
                  })}
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-center lg:text-left"
                >
                  About me
                </motion.h2>
                <motion.p
                  {...safeMotion({
                    initial: { opacity: 0, y: 20 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true },
                    transition: { duration: 0.6, delay: 0.2 }
                  })}
                  className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6 sm:mb-8 lg:mb-10 text-sm sm:text-base text-center lg:text-left"
                >
                  I'm a MERN stack web developer passionate about building intuitive, responsive, and user-friendly web applications. My journey into software development began through my academic studies in computer science, where I developed a strong foundation in programming and problem-solving. I work with HTML, CSS, JavaScript, React, Node.js, Express, and MongoDB to develop clean, scalable solutions. I focus on writing maintainable code, prioritizing user experience, and continuously learning to turn ideas into practical, real-world products.
                </motion.p>
                <motion.div
                  {...safeMotion({
                    initial: { opacity: 0, y: 20 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true },
                    transition: { duration: 0.6, delay: 0.4 }
                  })}
                  className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8 max-w-md sm:max-w-none mx-auto lg:mx-0"
                >
                  <div className="text-center lg:text-left">
                    <motion.div
                      {...safeMotion({
                        initial: { scale: 0 },
                        whileInView: { scale: 1 },
                        viewport: { once: true },
                        transition: { duration: 0.6, delay: 0.6 }
                      })}
                      className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-1 flex justify-center lg:justify-start items-center"
                    >
                      20 <span className="text-primary ml-1">+</span>
                    </motion.div>
                    <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide leading-tight">
                      Completed Projects
                    </div>
                  </div>
                  <div className="text-center lg:text-left">
                    <motion.div
                      {...safeMotion({
                        initial: { scale: 0 },
                        whileInView: { scale: 1 },
                        viewport: { once: true },
                        transition: { duration: 0.6, delay: 0.8 }
                      })}
                      className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-1 flex justify-center lg:justify-start items-center"
                    >
                      1 <span className="text-primary ml-1">+</span>
                    </motion.div>
                    <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide leading-tight">
                      Years of Experience
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section className="py-12 sm:py-16 lg:py-20 glass-section border-y border-white/10 overflow-hidden" id="skills">
          <div className="container mx-auto px-4 sm:px-6 md:px-10 lg:px-14 xl:px-18 w-full overflow-hidden">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center text-center mb-8 sm:mb-12 lg:mb-16"
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 relative inline-block glass-text">
                Technical Skills
                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 sm:w-16 h-1 bg-gradient-to-r from-primary to-blue-500 rounded-full glass-accent-line"></span>
              </h2>
              <p className="text-white/70 mt-4 lg:mt-6 max-w-lg text-sm sm:text-base px-4 sm:px-0">
                Visualizing my technical proficiency and toolset across the development stack.
              </p>
            </motion.div>
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 w-full"
            >
              {[
                {
                  icon: 'desktop_mac',
                  title: 'Frontend',
                  skills: [
                    { name: 'React', level: 90 },
                    { name: 'Tailwind CSS', level: 95 },
                    { name: 'JavaScript (ES6+)', level: 85 }
                  ]
                },
                {
                  icon: 'dns',
                  title: 'Backend',
                  skills: [
                    { name: 'Node.js', level: 80 },
                    { name: 'Express.js', level: 75 },
                    { name: 'Databases (MongoDb)', level: 70 }
                  ]
                },
                {
                  icon: 'build',
                  title: 'Tools',
                  skills: [
                    { name: 'Git / GitHub', level: 95 },
                    { name: 'Figma', level: 85 }
                  ]
                }
              ].map((category, index) => (
                <motion.div
                  key={category.title}
                  variants={fadeInUp}
                  whileHover={!shouldReduceMotion ? { y: -10 } : {}}
                  transition={{ duration: 0.3 }}
                  className="w-full"
                >
                  <Card className="p-4 sm:p-6 lg:p-8 glass-card border-0 hover:border-primary/40 transition-all duration-300 group h-full">
                    <motion.div
                      whileHover={!shouldReduceMotion ? { scale: 1.1, rotate: 5 } : {}}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-primary transition-colors duration-300"
                    >
                      <span className="material-icons-outlined text-primary group-hover:text-white text-xl sm:text-2xl transition-colors duration-300">{category.icon}</span>
                    </motion.div>
                    <CardTitle className="text-lg sm:text-xl mb-4 sm:mb-6 group-hover:text-white transition-colors text-white/90">{category.title}</CardTitle>
                    <CardContent className="space-y-3 sm:space-y-4 lg:space-y-5 p-0">
                      {category.skills.map((skill, skillIndex) => (
                        <div key={skill.name} className="space-y-1 sm:space-y-2">
                          <div className="flex justify-between text-xs sm:text-sm font-medium">
                            <span className="text-white/80 truncate pr-2">{skill.name}</span>
                            <span className="text-primary font-bold flex-shrink-0">{skill.level}%</span>
                          </div>
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${skill.level}%` }}
                            viewport={{ once: true }}
                            transition={{ 
                              duration: shouldReduceMotion ? 0.5 : 1, 
                              delay: shouldReduceMotion ? 0 : (index * 0.2 + skillIndex * 0.1) 
                            }}
                            className="glass-progress-container"
                          >
                            <div className="glass-progress-fill h-1.5 sm:h-2 rounded-full" style={{ width: `${skill.level}%` }}></div>
                          </motion.div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Education Section */}
        <section className="py-20 relative overflow-hidden glass-section-alt" id="education">
          <motion.div
            style={{ y: y1 }}
            className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10"
          ></motion.div>
          <motion.div
            style={{ y: y2 }}
            className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -z-10"
          ></motion.div>
          <div className="container mx-auto px-10 md:px-14 lg:px-18">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block glass-text">
                Educational Qualification
                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-primary to-purple-500 rounded-full glass-accent-line"></span>
              </h2>
              <p className="text-white/70 mt-6 max-w-lg">
                My academic journey.
              </p>
            </motion.div>
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {[
                {
                  period: '2023 - Present',
                  degree: 'Bachelor of Science(B.Sc.)',
                  institution: 'International University of Business Agriculture and Technology',
                  field: 'Computer Science & Engineering',
                  grade: 'Currently in 10th semester with a CGPA of 3.56',
                  icon: 'school'
                },
                {
                  period: '2021',
                  degree: 'HSC',
                  institution: 'Bangladesh Noubahini School & College',
                  field: 'Science',
                  grade: 'GPA: 5.00',
                  icon: 'menu_book'
                },
                {
                  period: '2019',
                  degree: 'SSC',
                  institution: 'Khulna Public College',
                  field: 'Science',
                  grade: 'GPA: 4.50',
                  icon: 'menu_book'
                }
              ].map((edu, index) => (
                <motion.div
                  key={edu.degree}
                  variants={fadeInUp}
                  whileHover={!shouldReduceMotion ? { y: -10, scale: 1.02 } : {}}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="p-8 glass-education-card border-0 shadow-lg hover:-translate-y-1 transition-transform duration-300 h-full flex flex-col relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-20">
                      <span className="material-icons-outlined text-6xl text-white">{edu.icon}</span>
                    </div>
                    <div className="mb-4">
                      <span className="glass-badge">{edu.period}</span>
                    </div>
                    <CardTitle className="text-xl mb-2 text-white">{edu.degree}</CardTitle>
                    <p className="text-white/70 text-sm font-medium mb-4">{edu.institution}</p>
                    <p className="text-white/90">{edu.field}</p>
                    <br></br>
                    <div className="mt-auto">
                      <p className="text-white/80 text-sm leading-relaxed">
                        {edu.grade}
                      </p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Projects Section */}
        <section className="py-20" id="projects">
          <div className="container mx-auto px-10 md:px-14 lg:px-18">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-3 relative inline-block glass-text">
                Projects
                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-primary to-pink-500 rounded-full glass-accent-line"></span>
              </h2>
              <p className="text-white/70 mt-6 max-w-lg">
                A selection of my recent work across mobile and web platforms.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 mb-8">
              {displayedProjects.map((project, index) => (
                <ProjectCard key={project.id} project={project} index={index} />
              ))}
            </div>

            {projectsData.length > 2 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex justify-center"
              >
                <Button
                  variant="outline"
                  className="px-8 py-3 border-gray-300 dark:border-gray-600 hover:border-primary hover:text-primary dark:text-white dark:hover:text-primary font-medium transition-all duration-300 flex items-center gap-2 group"
                  onClick={handleViewAllProjects}
                >
                  {showAllProjects ? 'Show Featured Projects' : 'View All Projects'}
                  <span className="material-icons-outlined text-lg group-hover:translate-x-1 transition-transform">
                    {showAllProjects ? 'visibility_off' : 'arrow_forward'}
                  </span>
                </Button>
              </motion.div>
            )}
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 glass-section border-y border-white/10" id="contacts">
          <div className="container mx-auto px-10 md:px-14 lg:px-18">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-3 relative inline-block glass-text">
                Contact Information
                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-primary to-cyan-500 rounded-full glass-accent-line"></span>
              </h2>
              <p className="text-white/70 mt-6 max-w-lg">
                Feel free to reach out for collaborations or just a friendly hello.
              </p>
            </motion.div>
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {[
                {
                  icon: 'email',
                  title: 'Email Me',
                  info: 'junaidhossain77349@gmail.com',
                  action: () => window.open('mailto:junaidhossain77349@gmail.com', '_blank')
                },
                {
                  icon: 'phone',
                  title: 'Call Me',
                  info: '+880 1333 662185',
                  action: () => window.open('tel:+8801333662185', '_blank')
                },
                {
                  icon: 'chat',
                  title: 'WhatsApp',
                  info: '+880 1791 512216',
                  action: () => window.open('https://wa.me/8801791512216', '_blank')
                }
              ].map((contact, index) => (
                <motion.div
                  key={contact.title}
                  variants={fadeInUp}
                  whileHover={!shouldReduceMotion ? { y: -10, scale: 1.02 } : {}}
                  transition={{ duration: 0.3 }}
                >
                  <Card
                    className="flex flex-col items-center p-8 glass-card border-0 hover:border-primary/50 transition-all duration-300 group text-center hover:-translate-y-1 shadow-lg shadow-gray-100 dark:shadow-none cursor-pointer"
                    onClick={contact.action}
                  >
                    <motion.div
                      whileHover={!shouldReduceMotion ? { scale: 1.1, rotate: 5 } : {}}
                      className="w-16 h-16 rounded-full glass-icon-container flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors duration-300"
                    >
                      <span className="material-icons-outlined text-3xl text-white group-hover:text-white transition-colors">{contact.icon}</span>
                    </motion.div>
                    <CardTitle className="text-xl mb-2 text-white">{contact.title}</CardTitle>
                    <p className="text-white/70 font-medium">{contact.info}</p>
                  </Card>
                </motion.div>
              ))}
            </motion.div>

            {/* Contact Form Button */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex justify-center mt-12"
            >
              <Button
                className="px-8 py-3 glass-button font-medium transition-all duration-300 flex items-center gap-2 group"
                onClick={() => setIsContactFormOpen(true)}
              >
                <span className="material-icons-outlined text-lg">email</span>
                Send Message
              </Button>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-surface-light dark:bg-surface-dark border-t border-gray-200 dark:border-gray-800"
      >
        <div className="container mx-auto px-10 md:px-14 lg:px-18 py-12">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8"
          >
            {/* Brand Section */}
            <motion.div variants={fadeInUp} className="md:col-span-2">
              <div className="text-2xl font-bold tracking-wide mb-4">
                Md Junaid <span className="text-primary">Hossain</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                Passionate MERN stack developer creating innovative solutions through clean code and thoughtful design. Let's build something amazing together.
              </p>
             <div className="flex space-x-4">
  {[
    { Icon: FaGithub, link: 'https://github.com/Junaid8217', label: 'GitHub' },
    { Icon: FaLinkedinIn, link: 'https://www.linkedin.com/in/md-junaid-hossain-623879360', label: 'LinkedIn' },
    { Icon: FaFacebookF, link: 'https://www.facebook.com/junaid.hossain.33821', label: 'Facebook' },
  ].map(({ Icon, link, label }) => (
    <motion.a
      key={label}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={!shouldReduceMotion ? { scale: 1.1, rotate: 5 } : {}}
      whileTap={{ scale: 0.95 }}
      aria-label={label}
    >
      <Button
        size="icon"
        variant="ghost"
        className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-primary dark:hover:bg-primary transition-colors"
      >
        <Icon className="text-lg text-gray-600 dark:text-gray-300 hover:text-white" />
      </Button>
    </motion.a>
  ))}
</div>


            </motion.div>

            {/* Quick Links */}
            <motion.div variants={fadeInUp}>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {[
                  { name: 'About', href: '#about' },
                  { name: 'Skills', href: '#skills' },
                  { name: 'Projects', href: '#projects' },
                  { name: 'Contact', href: '#contacts' }
                ].map((link, index) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <a href={link.href} className="text-gray-600 dark:text-gray-400 hover:text-primary transition-colors">{link.name}</a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div variants={fadeInUp}>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4">Services</h3>
              <ul className="space-y-2">
                {['Web Development', 'App Development', 'UI/UX Design', 'Consulting'].map((service, index) => (
                  <motion.li
                    key={service}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <span className="text-gray-600 dark:text-gray-400">{service}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </motion.div>

          {/* Bottom Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="pt-8 border-t border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-center"
          >
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 Md Junaid Hossain. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              
              <div className="flex items-center text-gray-500 dark:text-gray-400">
                <span className="material-icons-outlined text-sm mr-1">favorite</span>
                <span>Made with passion</span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.footer>

      {/* Contact Form Modal */}
      <ContactForm
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
    </div>
  );
}

export default App;